import React, { useState } from 'react'
import emailjs from '@emailjs/browser'
import ArrowMark from '@/components/ArrowMark'
import PageHeader from '@/components/PageHeader'
import SectionLabel from '@/components/SectionLabel'
import ArchLineBg from '@/components/ArchLineBg'

const MAPS_EMBED = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3532.546826377575!2d85.3323808753235!3d27.70039797618663!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39eb199f4dd6d27b%3A0xd380b712f1160ebb!2sS.M.T.%20Construction%20(P.)%20LTD!5e0!3m2!1sen!2snp!4v1780208640043!5m2!1sen!2snp"
const MAPS_PLACE = "https://www.google.com/maps/place/S.M.T.+Construction+(P.)+LTD/@27.7003979,85.3323808,17z/data=!3m1!4b1!4m6!3m5!1s0x39eb199f4dd6d27b:0xd380b712f1160ebb!8m2!3d27.7003979!4d85.3349557!16s%2Fg%2F11c1y8rk6_"
const MAPS_DIRECTIONS = "https://www.google.com/maps/dir//S.M.T.+Construction+(P.)+LTD,+Tanka+Prasad+Marg,+Kathmandu+44600/@27.7003979,85.3349557,17z"

function NepalMap() {
  return (
    <div>
      {/* Action bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', display: 'inline-block' }} />
          <span style={{ fontFamily: 'var(--space)', fontSize: 14, fontWeight: 600, color: 'var(--fg-0)' }}>SMT Construction (P.) Ltd</span>
          <span className="mono" style={{ color: 'var(--fg-3)', fontSize: 11 }}>Baneshwor, Kathmandu</span>
        </div>
        <div style={{ flex: 1 }} />
        <a
          href={MAPS_DIRECTIONS}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '9px 16px', background: 'var(--accent)', color: '#fff',
            textDecoration: 'none', borderRadius: 999,
            fontFamily: 'var(--text)', fontSize: 13, fontWeight: 500,
            transition: 'background .2s',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="3 11 22 2 13 21 11 13 3 11" />
          </svg>
          Get directions
        </a>
        <a
          href={MAPS_PLACE}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '9px 16px', background: '#fff', color: 'var(--fg-0)',
            textDecoration: 'none', borderRadius: 999,
            fontFamily: 'var(--text)', fontSize: 13, fontWeight: 500,
            border: '1px solid var(--line)',
            transition: 'border-color .2s',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
          Open in Google Maps
        </a>
      </div>

      {/* Map iframe */}
      <div style={{ position: 'relative', width: '100%', borderRadius: 16, overflow: 'hidden', border: '1px solid var(--line-soft)', boxShadow: '0 4px 24px -8px rgba(0,0,0,0.10)' }}>
        <iframe
          src={MAPS_EMBED}
          width="100%"
          height="480"
          style={{ border: 0, display: 'block' }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="SMT Construction office location"
        />
      </div>

      {/* Footer strip */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, flexWrap: 'wrap', gap: 8 }}>
        <span className="mono" style={{ color: 'var(--fg-3)', fontSize: 11 }}>27.7004° N · 85.3350° E · Baneshwor Height-10</span>
        <a href={MAPS_PLACE} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontSize: 12, fontFamily: 'var(--text)', fontWeight: 500, textDecoration: 'none' }}>
          View larger map ↗
        </a>
      </div>
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: '', org: '', email: '', type: 'residential', msg: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const upd = (k, v) => setForm({ ...form, [k]: v });
  const ok = form.name && form.email && form.msg;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ok) return;
    setStatus('sending');
    setErrorMsg('');

    const serviceId  = import.meta.env.VITE_EMAILJS_SERVICE_ID;
    const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
    const publicKey  = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey ||
        serviceId === 'your_service_id') {
      // EmailJS not configured yet — simulate success for development
      setTimeout(() => setStatus('success'), 800);
      return;
    }

    try {
      await emailjs.send(
        serviceId,
        templateId,
        {
          from_name:    form.name,
          from_org:     form.org || '—',
          from_email:   form.email,
          project_type: form.type,
          message:      form.msg,
        },
        publicKey,
      );
      setStatus('success');
    } catch (err) {
      console.error('EmailJS error:', err);
      setErrorMsg('Something went wrong. Please email us directly at info@smtconstruction.com');
      setStatus('error');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="contact-form" style={{ display: 'flex', flexDirection: 'column', gap: 0, border: '1px solid var(--line-soft)' }}>
      {status === 'success' ? (
        <div style={{ padding: 56, textAlign: 'center' }}>
          <div className="mono" style={{ color: 'var(--accent)' }}>ENQUIRY RECEIVED</div>
          <h3 className="display" style={{ fontSize: 32, color: 'var(--fg-0)', marginTop: 16 }}>Thank you, {form.name.split(' ')[0]}.</h3>
          <p style={{ marginTop: 16, color: 'var(--fg-2)' }}>We'll respond within one working day from our Baneshwor office.</p>
        </div>
      ) : (
        <>
          {[
            { k: 'name', label: 'Full name', placeholder: 'Your name' },
            { k: 'org', label: 'Organisation', placeholder: 'Embassy / agency / company' },
            { k: 'email', label: 'Email', placeholder: 'name@institution.org', type: 'email' },
          ].map((f, i) => (
            <label key={f.k} style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 16, padding: '20px 24px', borderBottom: '1px solid var(--line-soft)', alignItems: 'center' }}>
              <span className="mono" style={{ color: 'var(--fg-3)' }}>{String(i + 1).padStart(2, '0')} / {f.label.toUpperCase()}</span>
              <input type={f.type || 'text'} required value={form[f.k]} onChange={(e) => upd(f.k, e.target.value)} placeholder={f.placeholder}
                style={{ background: 'transparent', border: 'none', color: 'var(--fg-0)', fontFamily: 'var(--text)', fontSize: 16, outline: 'none', padding: 4 }} />
            </label>
          ))}
          <label style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 16, padding: '20px 24px', borderBottom: '1px solid var(--line-soft)', alignItems: 'center' }}>
            <span className="mono" style={{ color: 'var(--fg-3)' }}>04 / PROJECT TYPE</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[['residential', 'Home'], ['commercial', 'Commercial'], ['school', 'School'], ['hospital', 'Hospital'], ['renovation', 'Renovation'], ['embassy', 'Embassy / Agency'], ['other', 'Other']].map(([v, l]) => (
                <button key={v} type="button" onClick={() => upd('type', v)} style={{
                  background: form.type === v ? 'var(--accent)' : 'transparent', color: form.type === v ? '#F5F5F2' : 'var(--fg-1)',
                  border: '1px solid', borderColor: form.type === v ? 'var(--accent)' : 'var(--line)',
                  padding: '6px 12px', cursor: 'pointer', fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase',
                }}>{l}</button>
              ))}
            </div>
          </label>
          <label style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 16, padding: '20px 24px', borderBottom: '1px solid var(--line-soft)', alignItems: 'start' }}>
            <span className="mono" style={{ color: 'var(--fg-3)', paddingTop: 8 }}>05 / BRIEF</span>
            <textarea required value={form.msg} onChange={(e) => upd('msg', e.target.value)} placeholder="A few sentences on the site, scope, schedule, and any specific engineering or security requirements." rows={5}
              style={{ background: 'transparent', border: 'none', color: 'var(--fg-0)', fontFamily: 'var(--text)', fontSize: 15, outline: 'none', padding: 4, resize: 'vertical', lineHeight: 1.5 }} />
          </label>
          {status === 'error' && (
            <div style={{ margin: '0 24px 0', padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, fontSize: 13, color: '#b91c1c', lineHeight: 1.5 }}>
              {errorMsg}
            </div>
          )}
          <div style={{ padding: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--fg-2)', fontSize: 13 }}>We respond within one working day.</span>
            <button type="submit" disabled={!ok || status === 'sending'} className="btn btn-primary"
              style={{ opacity: ok && status !== 'sending' ? 1 : 0.4, cursor: ok && status !== 'sending' ? 'pointer' : 'not-allowed' }}>
              {status === 'sending' ? 'Sending…' : 'Send enquiry'}
              {status !== 'sending' && <span className="arrow-circle"><ArrowMark /></span>}
            </button>
          </div>
        </>
      )}
    </form>
  );
}

export default function ContactPage() {
  return (
    <div className="page">
      <PageHeader eyebrow="Contact"
        title={<>Let&rsquo;s build something <span style={{ color: 'var(--accent)' }}>that lasts</span>.</>}
        sub="A family home, a school, a hospital wing, a renovation — send us a brief and we&rsquo;ll respond within one working day from our Baneshwor office."
        badge="Response within 1 working day"
        variant="contact" />

      <section style={{ padding: '80px 0', borderTop: '1px solid var(--line-soft)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 64 }} className="contact-grid">
            <ContactForm />
            <aside style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <div className="panel" style={{ padding: 24 }}>
                <div className="mono" style={{ color: 'var(--accent)' }}>OFFICE / 01</div>
                <div className="display" style={{ fontSize: 22, marginTop: 12, color: 'var(--fg-0)' }}>Headquarters</div>
                <div style={{ marginTop: 16, fontSize: 13, color: 'var(--fg-1)', lineHeight: 1.7 }}>
                  Tanka Prasad Marg<br />
                  Baneshwor Height-10, Kathmandu · Nepal<br />
                  <span className="mono" style={{ color: 'var(--fg-2)' }}>27.6987° N · 85.3025° E</span>
                </div>
                <div className="hairline" style={{ margin: '20px 0' }} />
                <div style={{ fontSize: 13, color: 'var(--fg-1)', lineHeight: 1.7 }}>
                  T <span style={{ fontFamily: 'var(--mono)' }}>+977 1 4599344</span><br />
                  M <span style={{ fontFamily: 'var(--mono)' }}>+977 9851 144990</span><br />
                  P.O. Box <span style={{ fontFamily: 'var(--mono)' }}>3563</span><br />
                  E <span style={{ fontFamily: 'var(--mono)' }}>info@smtconstruction.com</span><br />
                  <span style={{ fontFamily: 'var(--mono)', color: 'var(--fg-2)' }}>sunmontueco@gmail.com</span>
                </div>
              </div>
              <div className="panel" style={{ padding: 24 }}>
                <div className="mono" style={{ color: 'var(--accent)' }}>REGISTRATIONS</div>
                <div style={{ marginTop: 12, fontSize: 12, color: 'var(--fg-1)', lineHeight: 1.85, fontFamily: 'var(--mono)' }}>
                  VAT · 500097003<br />
                  UNGM Vendor · 939303<br />
                  UEI · ZN2SYN8CRR16<br />
                  CAGE / NCAGE · SAXG9<br />
                  Registered · 2046-06-06 B.S.
                </div>
              </div>
              <div className="panel" style={{ padding: 24 }}>
                <div className="mono" style={{ color: 'var(--accent)' }}>HOURS</div>
                <div style={{ marginTop: 12, fontSize: 13, color: 'var(--fg-1)', lineHeight: 1.7 }}>
                  Sun – Fri · 09:00 – 18:00<br />
                  Saturday · Closed<br />
                  <span className="mono" style={{ color: 'var(--fg-2)' }}>NEPAL STANDARD TIME (UTC+05:45)</span>
                </div>
              </div>
              <div className="panel" style={{ padding: 24 }}>
                <div className="mono" style={{ color: 'var(--accent)' }}>RESPONSE SLA</div>
                <div style={{ marginTop: 12, fontSize: 13, color: 'var(--fg-1)', lineHeight: 1.7 }}>
                  Initial response within one working day. Site visit and feasibility within five working days for projects above NPR 100M.
                </div>
              </div>
            </aside>
          </div>
        </div>
        <style>{`@media (max-width: 900px) { .contact-grid { grid-template-columns: 1fr !important; } }`}</style>
      </section>

      <section style={{ padding: '80px 0', borderTop: '1px solid var(--line-soft)' }}>
        <div className="container">
          <SectionLabel title="LOCATION" count="KATHMANDU · BAGMATI" />
          <div style={{ marginTop: 48 }}>
            <NepalMap />
          </div>
        </div>
      </section>
    </div>
  );
}
